'use client';

import { IActiveNavConfig, IBlogConfig } from '@/api/types/blog';
import Accordion, { AccordionItemProps } from '@/components/Accordion';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import { useTranslations } from '@/i18n/use-translations';
import { BLOG_MARK_LIST } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Arrow_down, MinusOutlined, PlusOutlined } from '@zeelool/icon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BlogSearchInput from './BlogSearchInput';

interface BlogNavProps {
  activeNav?: IActiveNavConfig;
  navList?: IBlogConfig[];
  onTypeChange?: (nav: any) => void;
  onClose?: () => void;
  className?: string;
  title?: string;
  enableSearch?: boolean;
  searchKeyword?: string;
  onSearchSubmit?: (keyword: string) => void;
}

interface NavItem extends IBlogConfig {
  show?: boolean;
}

const BlogNav: React.FC<BlogNavProps> = ({
  activeNav = {},
  navList = [],
  onTypeChange = () => {},
  onClose = () => {},
  className = '',
  title = '',
  enableSearch = false,
  searchKeyword = '',
  onSearchSubmit,
}) => {
  const [nav, setNav] = useState<NavItem[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const router = useRouter();
  const t = useTranslations('blog.components');

  // 处理navList变化
  useEffect(() => {
    if (Array.isArray(navList) && navList.length) {
      // 先筛选出普通导航项并排序
      const commonNavList = navList
        .filter((item) => !['all_blogs', 'shop_zeelool'].includes(item.mark))
        .sort((a, b) => a.sort - b.sort);

      // 处理普通导航项
      const processedNav = commonNavList.map((item) => ({ ...item, show: true }));

      // 获取特殊导航项
      const allBlogsNav = navList.find((item) => item.mark === 'all_blogs');
      const shopZeeloolNav = navList.find((item) => item.mark === 'shop_zeelool');

      // 合并所有导航项
      const finalNav = [...processedNav];
      if (allBlogsNav) finalNav.push({ ...allBlogsNav, show: false });
      if (shopZeeloolNav) finalNav.push({ ...shopZeeloolNav, show: false });

      setNav(finalNav);

      // 默认展开所有有子项的导航
      const defaultExpanded = finalNav
        .filter((item) => item.content?.child && item.content.child.length > 0)
        .map((item) => item.id?.toString() || '');
      setExpandedKeys(defaultExpanded);
    }
  }, [navList]);

  // 判断是否为数组
  const isArray = (value: any): value is any[] => Array.isArray(value);

  // 处理子导航点击
  const handleChildClick = useCallback(
    (child: any, parentName: string) => {
      onTypeChange({ ...child, parent_name: parentName });
    },
    [onTypeChange],
  );

  // 判断一级导航是否激活
  const isLevelOneActive = useCallback(
    (item: NavItem) => {
      return (
        item.mark === activeNav.child_id && (!item.content?.child || !item.content.child.length)
      );
    },
    [activeNav.child_id],
  );

  // 处理简单导航项点击（没有子项的）
  const handleSimpleNavClick = useCallback(
    (item: NavItem) => {
      if ([BLOG_MARK_LIST.ALL_BLOGS, BLOG_MARK_LIST.SHOP_ZEELOOL].includes(item.mark)) {
        onTypeChange(item);
      }
    },
    [onTypeChange],
  );

  const handleSearchSubmit = useCallback(
    (keyword: string) => {
      onSearchSubmit?.(keyword);
      if (!onSearchSubmit) {
        router.push(`/blog/search-results?s=${encodeURIComponent(keyword)}`, { scroll: false });
      }
      onClose();
    },
    [onClose, onSearchSubmit, router],
  );

  // 转换为Accordion所需的数据格式
  const accordionItems: AccordionItemProps[] = useMemo(() => {
    return nav
      .filter((item) => isArray(item.content?.child) && item.content.child.length > 0)
      .map((item) => ({
        key: item.id?.toString() || '',
        title: (
          <div className="flex items-center">
            <span
              className={`
                filter-title-text font-bb block
                ${isLevelOneActive(item) ? 'active underline ml-8 bg-[rgba(199,255,87,0.5)]' : ''}
                ${isWeb ? 'text-16' : 'text-14 leading-17 !font-bold'}
              `}
            >
              {item.content?.name || ''}
            </span>
            {isLevelOneActive(item) && (
              <Arrow_down aria-hidden className="active-icon ml-[12px] text-14 rotate-270" />
            )}
          </div>
        ),
        content: (
          <ul className={`filter-child-list ${isWeb ? 'pt-20' : ''}`}>
            {item.content?.child?.map((child: any) => (
              <li
                key={child.child_id}
                className={`filter-child-item flex items-center ${isWeb ? 'mb-24 last:mb-0' : 'mt-12 first:mt-16'}`}
              >
                <Link
                  prefetch={false}
                  className={`
                    filter-child-text block cursor-pointer
                    ${child.child_id === activeNav.child_id ? 'active underline ml-8 !font-bold bg-[rgba(199,255,87,0.5)]' : ''}
                    ${isWeb ? 'text-14 hover:underline' : 'font-ir text-13 leading-20'}
                  `}
                  onClick={(e) => {
                    e.preventDefault();
                    handleChildClick(child, item.content?.name || '');
                  }}
                  href={`/blog/${child.another_name}`}
                >
                  {child.name}
                </Link>
                {child.child_id === activeNav.child_id && (
                  <Arrow_down aria-hidden className="active-icon ml-[11px] text-12 rotate-270" />
                )}
              </li>
            ))}
          </ul>
        ),
        // 使用自定义图标函数
        expandIcon: (expanded: boolean) =>
          expanded ? <MinusOutlined aria-hidden /> : <PlusOutlined aria-hidden />,
      }));
  }, [nav, activeNav.child_id, isWeb, isLevelOneActive, handleChildClick]);

  // 处理Accordion变化
  const handleAccordionChange = useCallback((keys: string | string[]) => {
    setExpandedKeys(Array.isArray(keys) ? keys : [keys]);
  }, []);

  const mobileDrawerCloseIcon = (
    <span aria-hidden className="relative block h-[14.77px] w-[14.77px]">
      <span className="absolute left-1/2 top-1/2 h-[1.38px] w-[17px] -translate-x-1/2 -translate-y-1/2 rotate-45 bg-primary-text" />
      <span className="absolute left-1/2 top-1/2 h-[1.38px] w-[17px] -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-primary-text" />
    </span>
  );

  return (
    <div
      className={`
        blog-nav
        ${className}
        ${isWeb ? 'pr-24 pb-[96px] pl-48 w-full' : 'bg-white w-[270px]'}
      `}
    >
      {title && (
        <h2 className={isWeb ? 'text-32 font-bb leading-39' : 'text-32 font-bb pt-40 pb-[8px]'}>
          {title}
        </h2>
      )}
      <WapOnly>
        {/* 头部关闭按钮 */}
        <div className="pop-header sticky top-0 z-10 box-border flex h-[42px] w-full items-start justify-end bg-white px-0 pb-0 pr-[19.23px] pt-[30px]">
          <button
            type="button"
            aria-label={t('closeNavigation')}
            className="flex h-[14.77px] w-[14.77px] cursor-pointer items-center justify-center border-0 bg-transparent p-0"
            onClick={onClose}
          >
            {mobileDrawerCloseIcon}
          </button>
        </div>
      </WapOnly>

      {/* 导航列表 */}
      <div className={`${!isWeb ? 'px-[13px] pr-[15px]' : ''}`}>
        {enableSearch && (
          <BlogSearchInput
            initialValue={searchKeyword}
            onSearch={handleSearchSubmit}
            className={isWeb ? 'mt-24' : 'mt-42 mb-24'}
          />
        )}
        {nav.map((item, index) => {
          // 如果是有子项的导航，使用Accordion渲染
          if (isArray(item.content?.child) && item.content.child.length > 0) {
            const accordionItem = accordionItems.find(
              (accItem) => accItem.key === item.id?.toString(),
            );
            if (!accordionItem) return null;

            return (
              <div key={item.id} className={`filter-item ${!isWeb && index > 0 ? 'mt-24' : ''}`}>
                <Accordion
                  items={[accordionItem]}
                  activeKey={expandedKeys}
                  onChange={handleAccordionChange}
                  accordion={false}
                  sx={{
                    '&.MuiAccordion-root': {
                      borderRadius: '0',
                      boxShadow: 'none',
                      mb: 0,
                      backgroundColor: isWeb ? 'transparent' : 'white',
                      '&:before': {
                        display: 'none',
                      },
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '0',
                      '&.Mui-expanded': {
                        margin: '0',
                      },
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: '#282828',
                      '& svg': {
                        fontSize: isWeb ? '18px' : '14px',
                        strokeWidth: isWeb ? undefined : 2.2,
                      },
                    },
                  }}
                  summaryStyle={{
                    padding: isWeb ? '32px 0 12px 0' : '0 0 6px 0',
                    minHeight: 'auto !important',
                    borderBottom: '1px solid #808080',
                  }}
                  detailsStyle={{
                    padding: 0,
                  }}
                />
              </div>
            );
          } else {
            // 简单导航项（没有子项的）
            return (
              <div key={item.id} className={`filter-item ${!isWeb && index > 0 ? 'mt-24' : ''}`}>
                <div
                  className={`
                    filter-title flex justify-between items-center border-b border-[#808080]
                    ${isWeb ? 'pt-32 pb-12' : 'pt-0 pb-6'}
                  `}
                >
                  <div className="filter-title-text-box flex items-center">
                    {[BLOG_MARK_LIST.ALL_BLOGS, BLOG_MARK_LIST.SHOP_ZEELOOL].includes(item.mark) ? (
                      <button
                        type="button"
                        onClick={() => handleSimpleNavClick(item)}
                        aria-pressed={isLevelOneActive(item)}
                        className={`filter-title-text block cursor-pointer border-0 p-0 text-left font-bb ${
                          isLevelOneActive(item)
                            ? 'active underline ml-8 bg-[rgba(199,255,87,0.5)]'
                            : ''
                        } ${isWeb ? 'text-16 hover:underline' : 'text-14 leading-17 !font-bold'}`}
                      >
                        {item.content?.name}
                      </button>
                    ) : (
                      <h3
                        className={`filter-title-text font-bb block ${
                          isLevelOneActive(item)
                            ? 'active underline ml-8 bg-[rgba(199,255,87,0.5)]'
                            : ''
                        } ${isWeb ? 'text-16' : 'text-14 leading-17 !font-bold'}`}
                      >
                        {item.content?.name}
                      </h3>
                    )}
                    {isLevelOneActive(item) && (
                      <Arrow_down
                        aria-hidden
                        className={`active-icon ml-[12px] rotate-270 ${isWeb ? 'text-14' : 'text-14 [&_*]:stroke-[2.2]'}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          }
        })}
        <WapOnly>
          <div className="h-60" />
        </WapOnly>
      </div>
    </div>
  );
};

export default BlogNav;
